import { useEffect, useRef, useState } from 'react';
import { Pagination } from '@/components/ui/Pagination';
import { Modal } from '@/components/ui/Modal';
import { TABLE_BAR_DENSITY, TD_DENSITY, TH_DENSITY } from '@/components/ui/tableDensity';
import { formatINR } from '@/lib/format';
import type { OsGroup, OsTotals, OsBucket } from '@/api/accounts';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { exportReport, useOnSignal, xl, type ExportCell } from './exportReport';

const BUCKET_TEXT: Record<OsBucket, string> = {
  d0: 'text-success',
  d1: 'text-warning',
  d2: 'text-warning',
  d3: 'text-danger',
};

/** Amount cell: formatted ₹ value, or an em-dash when zero. */
const amt = (v: number) => (v > 0 ? formatINR(v) : '—');

const HEAD = 'border-b-2 border-r border-[var(--border-color)] bg-orange-50 font-black uppercase tracking-wide text-accent whitespace-nowrap last:border-r-0 ' + TH_DENSITY;
const CELL = 'border-r border-[var(--border-color)] font-semibold whitespace-nowrap last:border-r-0 ' + TD_DENSITY;

/**
 * Merged ageing + invoice-wise outstanding table, styled to the app's table
 * language (SIS Stock): orange header band, bordered grid, striped/grouped
 * rows. Each party is a shaded, clickable summary row (ageing buckets); its
 * open invoices/bills are collapsed by default and expand beneath it. A
 * single-party filter auto-expands that group.
 */
export function OsAgeingTable({
  groups,
  totals,
  partyLabel,
  showTerms = false,
  filterParty = '',
  search = '',
  exportSignal,
  exportName,
}: {
  groups: OsGroup[];
  totals: OsTotals;
  partyLabel: string;
  showTerms?: boolean;
  filterParty?: string;
  search?: string;
  /** Bumped by the page's Export button; exports every matching party with its invoices. */
  exportSignal?: number;
  exportName?: string;
}) {
  const term = search.trim().toLowerCase();
  const matches = (g: OsGroup) =>
    !term ||
    g.code.toLowerCase().includes(term) ||
    g.name.toLowerCase().includes(term) ||
    g.invoices.some((i) => `${i.no} ${i.date}`.toLowerCase().includes(term));
  const shown = filterParty
    ? groups.filter((g) => g.name === filterParty)
    : groups.filter(matches);
  // The party whose bills / invoices are open in the month-wise popup.
  const [detail, setDetail] = useState<OsGroup | null>(null);

  // Excel: each party's ageing row followed by its open invoices/bills, then the grand total.
  const toast = useToast();
  useOnSignal(exportSignal, () => {
    if (!shown.length) { toast.push('Nothing to export for these filters.', 'info'); return; }
    const doc = showTerms ? 'Bill' : 'Invoice';
    const headers = ['Code', partyLabel, doc, 'Date', 'Age', 'Total OS', ...(showTerms ? ['Terms'] : []), '0–30', '31–60', '61–90', '90+'];
    const rows: ExportCell[][] = [];
    const bucketCols = (b: OsBucket | null, v: number, g?: OsGroup): ExportCell[] =>
      g ? [xl(g.d0), xl(g.d1), xl(g.d2), xl(g.d3)] : (['d0', 'd1', 'd2', 'd3'] as OsBucket[]).map((k) => (k === b ? xl(v) : null));
    for (const g of shown) {
      rows.push([g.code, g.name, `${g.invoices.length} open`, null, g.oldest, xl(g.total), ...(showTerms ? [g.terms ?? null] : []), ...bucketCols(null, 0, g)]);
      for (const inv of g.invoices) {
        rows.push([g.code, g.name, inv.no, inv.date, inv.age, xl(inv.amount), ...(showTerms ? [null] : []), ...bucketCols(inv.bucket, inv.amount)]);
      }
    }
    const sum = (k: 'd0' | 'd1' | 'd2' | 'd3' | 'total') => shown.reduce((n, g) => n + g[k], 0);
    rows.push(['', `Total — ${shown.length} ${partyLabel.toLowerCase()}s`, null, null, null, xl(sum('total')), ...(showTerms ? [null] : []), xl(sum('d0')), xl(sum('d1')), xl(sum('d2')), xl(sum('d3'))]);
    exportReport(exportName ?? `${partyLabel}_Outstanding`, `${partyLabel} Outstanding`, headers, rows)
      .catch((e) => toast.push(apiError(e, 'Export failed'), 'error'));
  });

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  useEffect(() => { setPage(1); }, [search, filterParty, shown.length]);
  const total = shown.length;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, lastPage);
  const pageGroups = shown.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  // Cap the scroll body so the pagination footer stays pinned at the viewport bottom.
  const scrollRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState('60vh');
  useEffect(() => {
    function update() {
      const el = scrollRef.current;
      if (!el) return;
      const docTop = el.getBoundingClientRect().top + window.scrollY;
      const footerH = footerRef.current?.getBoundingClientRect().height ?? 0;
      setMaxHeight(`${Math.max(160, window.innerHeight - docTop - footerH - 8)}px`);
    }
    update();
    const r = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    return () => { cancelAnimationFrame(r); window.removeEventListener('resize', update); };
  }, [total]);

  const colCount = showTerms ? 9 : 8;

  // The popup sits outside the card: the card's backdrop blur would otherwise trap a fixed overlay inside it.
  return (
    <>
    <div className="overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl">
      <div className={`flex items-center justify-between gap-2 border-b border-[var(--border-color)] ${TABLE_BAR_DENSITY}`}>
        <span className="text-xs text-[var(--text-muted)]">
          {shown.length} {partyLabel.toLowerCase()}{shown.length === 1 ? '' : 's'} · click a row to see its {showTerms ? 'bills' : 'invoices'} month by month
        </span>
      </div>
      <div
        ref={scrollRef}
        className="overflow-auto [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--border-color)]"
        style={{ maxHeight }}
      >
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              <th className={HEAD} style={{ textAlign: 'center' }}>Code</th>
              <th className={HEAD} style={{ textAlign: 'left' }}>{partyLabel}</th>
              <th className={HEAD} style={{ textAlign: 'right' }}>Total OS</th>
              {showTerms && <th className={HEAD} style={{ textAlign: 'center' }}>Terms</th>}
              <th className={HEAD} style={{ textAlign: 'right' }}>0–30</th>
              <th className={HEAD} style={{ textAlign: 'right' }}>31–60</th>
              <th className={HEAD} style={{ textAlign: 'right' }}>61–90</th>
              <th className={HEAD} style={{ textAlign: 'right' }}>90+</th>
              <th className={HEAD} style={{ textAlign: 'center' }}>Age</th>
            </tr>
          </thead>
          <tbody>
            {pageGroups.map((g) => (
              <tr
                key={g.code}
                className="cursor-pointer select-none border-b border-[var(--border-color)] font-bold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-soft)]"
                onClick={() => setDetail(g)}
                title={`See ${g.name}'s ${showTerms ? 'bills' : 'invoices'} month by month`}
              >
                <td className={`${CELL} text-center font-extrabold text-accent`}>{g.code}</td>
                <td className={CELL}>
                  {g.name}
                  <span className="ml-1.5 text-[10px] font-normal text-[var(--text-muted)]">({g.invoices.length})</span>
                </td>
                <td className={`${CELL} text-right tabular-nums`}>{formatINR(g.total)}</td>
                {showTerms && <td className={`${CELL} text-center`}>{g.terms ?? '—'}</td>}
                <td className={`${CELL} text-right tabular-nums text-success`}>{amt(g.d0)}</td>
                <td className={`${CELL} text-right tabular-nums text-warning`}>{amt(g.d1)}</td>
                <td className={`${CELL} text-right tabular-nums text-warning`}>{amt(g.d2)}</td>
                <td className={`${CELL} text-right tabular-nums text-danger`}>{amt(g.d3)}</td>
                <td className={`${CELL} text-center tabular-nums`}>{g.oldest}</td>
              </tr>
            ))}
            {shown.length === 0 && (
              <tr><td className="px-4 py-10 text-center text-sm text-[var(--text-muted)]" colSpan={colCount}>No {partyLabel.toLowerCase()} matches.</td></tr>
            )}
          </tbody>
          {!filterParty && !term && (
            <tfoot>
              <tr className="bg-[var(--bg-secondary)] font-extrabold text-[var(--text-primary)]">
                <td className={`${CELL} border-t-2`} colSpan={2}>Total — {groups.length} {partyLabel.toLowerCase()}s</td>
                <td className={`${CELL} border-t-2 text-right tabular-nums`}>{formatINR(totals.total)}</td>
                {showTerms && <td className={`${CELL} border-t-2`} />}
                <td className={`${CELL} border-t-2 text-right tabular-nums`}>{formatINR(totals.d0)}</td>
                <td className={`${CELL} border-t-2 text-right tabular-nums`}>{formatINR(totals.d1)}</td>
                <td className={`${CELL} border-t-2 text-right tabular-nums`}>{formatINR(totals.d2)}</td>
                <td className={`${CELL} border-t-2 text-right tabular-nums`}>{formatINR(totals.d3)}</td>
                <td className={`${CELL} border-t-2`} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {total > 0 && (
        <div ref={footerRef} className="border-t border-[var(--border-color)] px-3 py-2 sm:px-5">
          <Pagination page={pageSafe} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} />
        </div>
      )}
    </div>
    <OsMonthPopup group={detail} showTerms={showTerms} onClose={() => setDetail(null)} />
    </>
  );
}

const BUCKET_LABEL: Record<OsBucket, string> = { d0: '0–30', d1: '31–60', d2: '61–90', d3: '90+' };
/** 'dd-mm-yyyy' → 'yyyy-mm' (sortable month key). */
const monthKey = (d: string) => `${d.slice(6, 10)}-${d.slice(3, 5)}`;
const monthName = (k: string) => new Date(+k.slice(0, 4), +k.slice(5, 7) - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

/** One party's open bills / invoices, grouped by the month they were raised, newest month first. */
function OsMonthPopup({ group, showTerms, onClose }: { group: OsGroup | null; showTerms: boolean; onClose: () => void }) {
  if (!group) return null;
  const doc = showTerms ? 'Bill' : 'Invoice';
  const months = new Map<string, typeof group.invoices>();
  for (const inv of [...group.invoices].sort((a, b) => monthKey(b.date).localeCompare(monthKey(a.date)) || b.date.slice(0, 2).localeCompare(a.date.slice(0, 2)))) {
    const k = monthKey(inv.date);
    months.set(k, [...(months.get(k) ?? []), inv]);
  }
  const chip = (label: string, value: string, cls = 'text-[var(--text-primary)]') => (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
      <div className={`text-sm font-extrabold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
  return (
    <Modal open onClose={onClose} size="lg" title={<span><span className="text-accent">{group.code}</span> · {group.name}</span>}>
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {chip('Total OS', `₹${formatINR(group.total)}`)}
        {showTerms && chip('Terms', group.terms ?? '—')}
        {chip('0–30', amt(group.d0), 'text-success')}
        {chip('31–60', amt(group.d1), 'text-warning')}
        {chip('61–90', amt(group.d2), 'text-warning')}
        {chip('90+', amt(group.d3), 'text-danger')}
        {chip('Oldest', group.oldest)}
      </div>
      <div className="space-y-4">
        {[...months.entries()].map(([k, invs]) => (
          <section key={k}>
            <div className="mb-1.5 flex items-baseline justify-between gap-2">
              <h4 className="text-sm font-extrabold text-[var(--text-primary)]">{monthName(k)}</h4>
              <span className="text-xs text-[var(--text-muted)]">
                {invs.length} {doc.toLowerCase()}{invs.length === 1 ? '' : 's'} · <b className="text-[var(--text-primary)]">₹{formatINR(invs.reduce((n, i) => n + i.amount, 0))}</b>
              </span>
            </div>
            <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className={HEAD} style={{ textAlign: 'left' }}>{doc} no.</th>
                    <th className={HEAD} style={{ textAlign: 'left' }}>Date</th>
                    <th className={HEAD} style={{ textAlign: 'center' }}>Age</th>
                    <th className={HEAD} style={{ textAlign: 'center' }}>Ageing</th>
                    <th className={HEAD} style={{ textAlign: 'right' }}>Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {invs.map((inv) => (
                    <tr key={inv.no + inv.date} className="border-b border-[var(--border-color)] last:border-b-0">
                      <td className={`${CELL} font-bold text-accent`}>{inv.no}</td>
                      <td className={CELL}>{inv.date}</td>
                      <td className={`${CELL} text-center tabular-nums ${BUCKET_TEXT[inv.bucket]}`}>{inv.age}</td>
                      <td className={`${CELL} text-center ${BUCKET_TEXT[inv.bucket]}`}>{BUCKET_LABEL[inv.bucket]} days</td>
                      <td className={`${CELL} text-right tabular-nums text-[var(--text-primary)]`}>{formatINR(inv.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ))}
        {group.invoices.length === 0 && <p className="py-6 text-center text-sm text-[var(--text-muted)]">No open {doc.toLowerCase()}s.</p>}
      </div>
    </Modal>
  );
}
